import { IDimension } from 'core/models/report-redux';
import { ReduxModel } from 'core/models';
import { ICustomHierarchy, ICustomHierarchyColumnNode } from 'core/models/report-builder/report-builder';
import store from 'redux-v2/store';
import { IColumn } from 'core/models/report-response';
import { DimensionStatus } from 'core/constants/report';

/**
 * Selector to get custom hierarchies from Redux store
 * @param state - Global Redux state
 * @returns Array of custom hierarchies or empty array
 */
const customHierarchiesSelector = (state: ReduxModel.IGlobalState): ICustomHierarchy[] => {
  // Builder/preview flow holds hierarchies in reportBuilder.updatedReportBuilderInfo.
  // Saved-report and drilldown flows hold them on reportStoreV2.reportConfig.CustomHierarchies.
  const builderHierarchies = state.reportBuilder?.updatedReportBuilderInfo?.customHierarchies;
  if (builderHierarchies && builderHierarchies.length > 0) return builderHierarchies;
  return state.reportStoreV2?.reportConfig?.CustomHierarchies || [];
};

/**
 * Get all custom hierarchies from the Redux store
 * @returns Array of custom hierarchies
 */
const getCustomHierarchies = (): ICustomHierarchy[] => {
  const state = store.getState() as any as ReduxModel.IGlobalState;
  return customHierarchiesSelector(state);
};

/**
 * Get a specific hierarchy by its ID
 * @param hierarchyId - The GUID of the hierarchy
 * @returns The hierarchy object or undefined if not found
 */
const getHierarchyById = (hierarchyId: string): ICustomHierarchy | undefined => {
  const hierarchies = getCustomHierarchies();
  return hierarchies.find((hierarchy) => hierarchy.HierarchyID === hierarchyId);
};

/**
 * Get column information for a specific column in a hierarchy
 * @param hierarchyId - The GUID of the hierarchy
 * @param colId - The column ID
 * @returns The column node information or undefined if not found or doesn't belong to hierarchy
 */
const getColumnInfo = (hierarchyId: string, colId: string): ICustomHierarchyColumnNode | undefined => {
  const hierarchy = getHierarchyById(hierarchyId);
  if (!hierarchy) return undefined;

  const columnNode = hierarchy.ColumnsByID[colId];

  if (columnNode) {
    // Verify that the column actually belongs to this hierarchy by checking CustomHierarchyId
    const column = columnNode.Column;
    if (column?.Props?.CustomHierarchyId === hierarchyId) {
      return columnNode;
    }

    // Column exists in hierarchy object but doesn't have matching CustomHierarchyId
    return undefined;
  }

  return undefined;
};

/**
 * Get all column nodes from a hierarchy (excluding fixed properties)
 * Only returns columns that have matching CustomHierarchyId
 * @param hierarchyId - The GUID of the hierarchy
 * @returns Array of column nodes with their IDs that belong to this hierarchy
 */
const getAllColumnNodes = (hierarchyId: string): Array<{ colId: string; node: ICustomHierarchyColumnNode }> => {
  const hierarchy = getHierarchyById(hierarchyId);
  if (!hierarchy) return [];

  const columnNodes: Array<{ colId: string; node: ICustomHierarchyColumnNode }> = [];

  Object.keys(hierarchy.ColumnsByID).forEach((key) => {
    const node = hierarchy.ColumnsByID[key];

    // Only include columns that belong to this hierarchy
    if (node.Column?.Props?.CustomHierarchyId === hierarchyId) {
      columnNodes.push({
        colId: key,
        node,
      });
    }
  });

  return columnNodes;
};

/**
 * Get the primary column ID for a hierarchy
 * @param hierarchyId - The GUID of the hierarchy
 * @returns The primary column ID or undefined
 */
const getPrimaryColId = (hierarchyId: string): string | undefined => {
  const hierarchy = getHierarchyById(hierarchyId);
  return hierarchy?.PrimaryColID;
};

/**
 * Get the leaf column ID for a hierarchy
 * @param hierarchyId - The GUID of the hierarchy
 * @returns The leaf column ID or undefined
 */
const getLeafColId = (hierarchyId: string): string | undefined => {
  const hierarchy = getHierarchyById(hierarchyId);
  return hierarchy?.LeafColID;
};

/**
 * Get the next column in the hierarchy chain
 * @param hierarchyId - The GUID of the hierarchy
 * @param currentColId - The current column ID
 * @returns The next column node or undefined
 */
const getNextColumn = (hierarchyId: string, currentColId: string): ICustomHierarchyColumnNode | undefined => {
  const currentColumn = getColumnInfo(hierarchyId, currentColId);
  if (!currentColumn || !currentColumn.NextColID) return undefined;

  return getColumnInfo(hierarchyId, currentColumn.NextColID);
};

/**
 * Get the previous column in the hierarchy chain
 * @param hierarchyId - The GUID of the hierarchy
 * @param currentColId - The current column ID
 * @returns The previous column node or undefined
 */
const getPreviousColumn = (hierarchyId: string, currentColId: string): ICustomHierarchyColumnNode | undefined => {
  const currentColumn = getColumnInfo(hierarchyId, currentColId);
  if (!currentColumn || !currentColumn.PrevColID) return undefined;

  return getColumnInfo(hierarchyId, currentColumn.PrevColID);
};

/**
 * Get the hierarchy chain as an ordered array of column nodes
 * @param hierarchyId - The GUID of the hierarchy
 * @returns Ordered array of column nodes from primary to leaf
 */
const getHierarchyChain = (hierarchyId: string): Array<{ colId: string; node: ICustomHierarchyColumnNode }> => {
  const hierarchy = getHierarchyById(hierarchyId);
  if (!hierarchy || !hierarchy.PrimaryColID) return [];

  const chain: Array<{ colId: string; node: ICustomHierarchyColumnNode }> = [];
  let currentColId: string | null = hierarchy.PrimaryColID;

  while (currentColId) {
    const columnNode = getColumnInfo(hierarchyId, currentColId);
    if (!columnNode) break;

    chain.push({ colId: currentColId, node: columnNode });
    currentColId = columnNode.NextColID;
  }

  return chain;
};

/**
 * Check if a hierarchy exists
 * @param hierarchyId - The GUID of the hierarchy
 * @returns True if hierarchy exists, false otherwise
 */
const hierarchyExists = (hierarchyId: string): boolean => getHierarchyById(hierarchyId) !== undefined;

/**
 * Get hierarchy name by ID
 * @param hierarchyId - The GUID of the hierarchy
 * @returns The hierarchy name or undefined
 */
const getHierarchyName = (hierarchyId: string): string | undefined => {
  const hierarchy = getHierarchyById(hierarchyId);
  return hierarchy?.HierarchyName;
};

/**
 * Check if a column belongs to a hierarchy with the given column or dimension config
 * @param hierarchyId - The GUID of the hierarchy
 * @param col - The column or dimension config
 * @returns True if the column belongs to the hierarchy, false otherwise
 */
const checkIfColumnBelongsToHierarchy = (hierarchyId: string, col: IDimension | IColumn): boolean => {
  const hierarchy = getHierarchyById(hierarchyId);
  if (!hierarchy) return false;

  return hierarchy.ColumnsByID[col?.Id]?.Column?.Props?.CustomHierarchyId === hierarchyId;
};

/**
 * Calculates new columns by replacing parent column with child column for custom hierarchy drilldown
 * @param currentColumns - Current columns array
 * @param parentColumnId - The parent column ID to replace
 * @param hierarchyId - The hierarchy ID
 * @returns New columns array with parent replaced by child at the same position
 */
const calculateCustomHierarchyDrilldownColumns = (
  currentColumns: Array<IColumn>,
  parentColumnId: string,
  hierarchyId: string,
): Array<IColumn> => {
  if (!currentColumns) return currentColumns;

  // Get the next column in the hierarchy
  const nextColumn = getNextColumn(hierarchyId, parentColumnId);

  if (!nextColumn) {
    // No child column, return current columns
    return currentColumns;
  }

  // Replace parent column with child column at the same position
  return currentColumns.map((col) => {
    if (col.Id === parentColumnId) {
      // Return the child column with all its properties
      return nextColumn.Column as IColumn;
    }
    return col;
  });
};

/**
 * Pure function version: Get column information from hierarchies array without accessing store
 * @param hierarchies - Array of custom hierarchies
 * @param hierarchyId - The GUID of the hierarchy
 * @param colId - The column ID
 * @returns The column node information or undefined if not found
 */
const getColumnInfoFromHierarchies = (
  hierarchies: ICustomHierarchy[],
  hierarchyId: string,
  colId: string,
): ICustomHierarchyColumnNode | undefined => {
  if (!hierarchies || !hierarchies.length) return undefined;

  const hierarchy = hierarchies.find((h) => h.HierarchyID === hierarchyId);
  if (!hierarchy) return undefined;

  const columnNode = hierarchy.ColumnsByID[colId];

  if (columnNode) {
    // Verify that the column actually belongs to this hierarchy by checking CustomHierarchyId
    const column = columnNode.Column;
    if (column?.Props?.CustomHierarchyId === hierarchyId) {
      return columnNode;
    }

    // Column exists in hierarchy object but doesn't have matching CustomHierarchyId
    return undefined;
  }

  return undefined;
};

/**
 * Pure function version: Get the next column in the hierarchy chain without accessing store
 * @param hierarchies - Array of custom hierarchies
 * @param hierarchyId - The GUID of the hierarchy
 * @param currentColId - The current column ID
 * @returns The next column node or undefined
 */
const getNextColumnFromHierarchies = (
  hierarchies: ICustomHierarchy[],
  hierarchyId: string,
  currentColId: string,
): ICustomHierarchyColumnNode | undefined => {
  const currentColumn = getColumnInfoFromHierarchies(hierarchies, hierarchyId, currentColId);
  if (!currentColumn || !currentColumn.NextColID) return undefined;

  return getColumnInfoFromHierarchies(hierarchies, hierarchyId, currentColumn.NextColID);
};

/**
 * Pure function version: Get the previous column in the hierarchy chain without accessing store
 * @param hierarchies - Array of custom hierarchies
 * @param hierarchyId - The GUID of the hierarchy
 * @param currentColId - The current column ID
 * @returns The previous column node or undefined
 */
const getPreviousColumnFromHierarchies = (
  hierarchies: ICustomHierarchy[],
  hierarchyId: string,
  currentColId: string,
): ICustomHierarchyColumnNode | undefined => {
  const currentColumn = getColumnInfoFromHierarchies(hierarchies, hierarchyId, currentColId);
  if (!currentColumn || !currentColumn.PrevColID) return undefined;

  return getColumnInfoFromHierarchies(hierarchies, hierarchyId, currentColumn.PrevColID);
};

/**
 * Pure function version: Calculates new columns by replacing parent column with child column
 * This version accepts hierarchies as a parameter and doesn't access the store
 * Use this version within Redux reducers
 * @param hierarchies - Array of custom hierarchies
 * @param currentColumns - Current columns array
 * @param parentColumnId - The parent column ID to replace
 * @param hierarchyId - The hierarchy ID
 * @returns New columns array with parent replaced by child at the same position
 */
const calculateCustomHierarchyDrilldownColumnsFromHierarchies = (
  hierarchies: ICustomHierarchy[],
  currentColumns: Array<IColumn>,
  parentColumnId: string,
  hierarchyId: string,
  direction: 'next' | 'previous' = 'next',
): Array<IColumn> => {
  if (!currentColumns || !hierarchies) return currentColumns;

  let updatedColumnData: ICustomHierarchyColumnNode | undefined;
  if (direction === 'next') {
    if (!parentColumnId) {
      const activeHierarchy = hierarchies.find((h) => h.HierarchyID === hierarchyId);
      const primaryColId = activeHierarchy?.PrimaryColID;
      const primaryColNode = activeHierarchy?.ColumnsByID[primaryColId];
      updatedColumnData = {
        Column: primaryColNode?.Column as IColumn | IDimension,
        NextColID: primaryColNode?.NextColID,
        PrevColID: primaryColNode?.PrevColID,
      };
    } else {
      updatedColumnData = getNextColumnFromHierarchies(hierarchies, hierarchyId, parentColumnId);
    }
  } else {
    updatedColumnData = getPreviousColumnFromHierarchies(hierarchies, hierarchyId, parentColumnId);
  }

  if (!updatedColumnData) {
    // No child column, return current columns
    return currentColumns;
  }

  // Replace parent column with child column at the same position.
  const activeHierarchy = hierarchies.find((h) => h.HierarchyID === hierarchyId);
  return currentColumns.map((col) => {
    if (col.Props.CustomHierarchyId === hierarchyId) {
      // Return the child column with all its properties
      let updatedColumn = { ...updatedColumnData.Column, Name: activeHierarchy?.HierarchyName } as IColumn;
      const isUpdatedColumnLeafColumn = activeHierarchy?.LeafColID === updatedColumn.Id;
      if (isUpdatedColumnLeafColumn) {
        updatedColumn = {
          ...updatedColumn,
          Props: {
            ...updatedColumn.Props,
            Action: null, // action is set to null so that the leaf column in a custom hierarchy is not displayed as clickable in the grid
          },
        };
      }

      return {
        ...updatedColumn,
        Props: {
          ...updatedColumn.Props,
          CustomHierarchyId: hierarchyId,
          Description: updatedColumn.Props?.Description
            || activeHierarchy?.HierarchyDescription
            || col?.Props?.Description,
          /**
           * explicitly set the dimension status to applied for the next column in the hierarchy
           * as it will be displayed in the drilled down custom hierarchy report
           */
          Dimension: {
            ...updatedColumn.Props.Dimension,
            Applied: DimensionStatus.Applied,
          },
        },
      };
    }
    return col;
  });
};

export {
  customHierarchiesSelector,
  getCustomHierarchies,
  getHierarchyById,
  getColumnInfo,
  getAllColumnNodes,
  getPrimaryColId,
  getLeafColId,
  getNextColumn,
  getPreviousColumn,
  getHierarchyChain,
  hierarchyExists,
  getHierarchyName,
  checkIfColumnBelongsToHierarchy,
  calculateCustomHierarchyDrilldownColumns,
  getColumnInfoFromHierarchies,
  getNextColumnFromHierarchies,
  calculateCustomHierarchyDrilldownColumnsFromHierarchies,
};
